'use client';

import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { GoSeeForYourselfPrompt } from '../../user-dashboard/components/Plot4GardenEnhancements';
import { ObservationCompleteCosmologyNote, RootAnalysisExpander } from '../../open-letter/components/Plot5Enhancements';

// ─── TYPES ───────────────────────────────────────────────────────────────────

interface ObsFormData {
  title: string;
  city: string;
  encounter: string;
  q_is: string;
  q_what: string;
  q_when: string;
  selectedNames: string[];
  nameRoles: Record<string, string>;
  processName: string;
  processSteps: string[];
  zahir: string;
  batin: string;
  haqq: string;
  amanah: string;
  mrgBridges: string[];
}

interface ObsSubmissionProps {
  open: boolean;
  onClose: () => void;
  cityId: string;
  variant: string;
  prefilledTile?: {
    title: string;
    encounter?: string;
    names?: string[];
  } | null;
}

// ─── NAMES DATA ───────────────────────────────────────────────────────────────

const QUICK_NAMES = [
  { id: 'razzaq', ar: 'الرَّزَّاق', en: 'Ar-Razzaq', m: 'The Ever Provider' },
  { id: 'khaliq', ar: 'الْخَالِق', en: 'Al-Khaliq', m: 'The Creator' },
  { id: 'bari', ar: 'الْبَارِئ', en: 'Al-Bari', m: 'The Evolver' },
  { id: 'musawwir', ar: 'الْمُصَوِّر', en: 'Al-Musawwir', m: 'The Fashioner' },
  { id: 'latif', ar: 'اللَّطِيف', en: 'Al-Latif', m: 'The Subtly Kind' },
  { id: 'hakeem', ar: 'الْحَكِيم', en: 'Al-Hakeem', m: 'The All-Wise' },
  { id: 'wadud', ar: 'الْوَدُود', en: 'Al-Wadud', m: 'The Loving' },
  { id: 'jami', ar: 'الْجَامِع', en: 'Al-Jami', m: 'The Gatherer' },
  { id: 'hadi', ar: 'الْهَادِي', en: 'Al-Hadi', m: 'The Guide' },
  { id: 'adl', ar: 'الْعَدْل', en: 'Al-Adl', m: 'The Just' },
  { id: 'hafiz', ar: 'الْحَفِيظ', en: 'Al-Hafiz', m: 'The Preserver' },
  { id: 'muhyi', ar: 'الْمُحْيِي', en: 'Al-Muhyi', m: 'The Giver of Life' },
  { id: 'sabur', ar: 'الصَّبُور', en: 'As-Sabur', m: 'The Patient' },
  { id: 'nur', ar: 'النُّور', en: 'An-Nur', m: 'The Light' },
  { id: 'qadir', ar: 'الْقَادِر', en: 'Al-Qadir', m: 'The All-Capable' },
  { id: 'basir', ar: 'الْبَصِير', en: 'Al-Basir', m: 'The All-Seeing' },
  { id: 'alim', ar: 'الْعَلِيم', en: 'Al-Alim', m: 'The All-Knowing' },
  { id: 'haqq', ar: 'الْحَقّ', en: 'Al-Haqq', m: 'The Real' },
  { id: 'wahhab', ar: 'الْوَهَّاب', en: 'Al-Wahhab', m: 'The Bestower' },
  { id: 'muid', ar: 'الْمُعِيد', en: 'Al-Mu\'id', m: 'The Restorer' },
];

const MRG_CATEGORIES = [
  'Food & Nutrition', 'Housing & Shelter', 'Health & Wellness',
  'Education & Learning', 'Community & Connection', 'Justice & Advocacy',
  'Environment & Ecology', 'Economic Empowerment', 'Spiritual Development',
];

const LAYER_STEPS = [
  { id: 'encounter', num: 1, label: 'ENCOUNTER', ar: 'مُشَاهَدَة', desc: 'What do you perceive? Describe the sensory encounter — what you see, hear, smell, feel.' },
  { id: 'three-q', num: 2, label: 'THREE QUESTIONS', ar: 'ثَلَاثَة أَسْئِلَة', desc: 'Apply the three questions to this encounter.' },
  { id: 'names', num: 3, label: 'NAMES FISSION', ar: 'انْشِطَار الْأَسْمَاء', desc: 'Which Names of Allah are active in this encounter? Select all that apply.' },
  { id: 'sunnat', num: 4, label: 'SUNNAT ALLAH', ar: 'سُنَّة اللَّه', desc: 'What is the process this thing participates in? Name it and describe its steps.' },
  { id: 'zahir-batin', num: 5, label: 'ZAHIR · BATIN', ar: 'ظَاهِر · بَاطِن', desc: 'What is apparent (Zahir)? What is hidden (Batin)?' },
  { id: 'haqq', num: 6, label: 'HAQQ', ar: 'الْحَقّ', desc: 'What is the truth of this encounter? What does it actually declare?' },
  { id: 'amanah', num: 7, label: 'AMANAH + MRG', ar: 'الْأَمَانَة', desc: 'What trust does this recognition carry? How does it bridge to community action?' },
  { id: 'bridge', num: 8, label: 'BRIDGE LINKS', ar: 'رَوَابِط الْجُسُور', desc: 'What other haals does this connect to? Which MRG categories does it bridge?' },
];

// ─── OBSERVATION MODES ────────────────────────────────────────────────────────

type ObservationMode = 'full' | 'field-notes' | 'commentary' | 'sample-examination';

interface ObsModeConfig {
  id: ObservationMode;
  label: string;
  arabicLabel: string;
  description: string;
  layers: string[];
  color: string;
  icon: string;
}

const OBSERVATION_MODES: ObsModeConfig[] = [
  {
    id: 'full',
    label: 'Full Examination',
    arabicLabel: 'الفَحْص الكَامِل',
    description: 'The complete 8-layer recognition pathway. Encounter → Three Questions → Names → Sunnat → Zahir/Batin → Haqq → Amanah → Bridge.',
    layers: ['encounter', 'three-q', 'names', 'sunnat', 'zahir-batin', 'haqq', 'amanah', 'bridge'],
    color: '#c9a84c',
    icon: '◈',
  },
  {
    id: 'field-notes',
    label: 'Field Notes',
    arabicLabel: 'مُلَاحَظَات مَيْدَانِيَّة',
    description: 'Raw, unprocessed documentation of what was encountered. The sensory data, the photograph, the location. Evidence before it\'s been processed.',
    layers: ['encounter'],
    color: '#2d7a47',
    icon: '◎',
  },
  {
    id: 'commentary',
    label: 'Commentary',
    arabicLabel: 'تَعْلِيق',
    description: 'The caretaker\'s reflection on what they\'ve seen. The bridge from observation to understanding. Submit without a formal examination.',
    layers: ['encounter', 'three-q', 'names'],
    color: '#2d8a87',
    icon: '⬡',
  },
  {
    id: 'sample-examination',
    label: 'Sample Examination',
    arabicLabel: 'فَحْص عَيِّنَة',
    description: 'A focused look at a specific encounter. The IRL Lens at work — Zahir layers, Three Questions, Name extraction.',
    layers: ['encounter', 'three-q', 'names', 'zahir-batin', 'haqq'],
    color: '#8a8ab8',
    icon: '✦',
  },
];

// ─── MODE SELECTOR ────────────────────────────────────────────────────────────

function ObservationModeSelector({ onSelect }: { onSelect: (mode: ObservationMode) => void }) {
  return (
    <div style={{
      position: 'absolute', inset: 0, background: 'rgba(6,13,18,0.98)',
      zIndex: 51, display: 'flex', flexDirection: 'column',
      fontFamily: "'Courier New', monospace",
      padding: '24px 20px',
      overflowY: 'auto',
    }}>
      {/* Operating ethos */}
      <div style={{
        background: 'rgba(201,168,76,0.04)',
        border: '1px solid rgba(201,168,76,0.15)',
        borderLeft: '3px solid rgba(201,168,76,0.4)',
        borderRadius: '0 6px 6px 0',
        padding: '14px 16px',
        marginBottom: 24,
      }}>
        <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 8 }}>
          ◈ IRL LENS — OPERATING ETHOS
        </div>
        <p style={{
          fontFamily: "'Georgia', serif",
          fontStyle: 'italic',
          fontSize: 13,
          color: '#d4c5a9',
          lineHeight: 1.8,
          marginBottom: 4,
        }}>
          "Understand the dimensions of a space, state, thing, its garment and grasping the measurements that it's given."
        </p>
        <p style={{ fontSize: 9, color: '#7a6f5d' }}>— Open Letter to Imprisoned Caretakers, Section 2</p>
      </div>

      <div style={{ fontSize: 9, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 6 }}>
        SELECT OBSERVATION MODE
      </div>
      <p style={{ fontSize: 11, color: '#7a6f5d', lineHeight: 1.6, marginBottom: 20 }}>
        Not every encounter needs the full 8-layer treatment. Sometimes you just need to write down what you saw. Field notes are valid.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {OBSERVATION_MODES.map(mode => (
          <button
            key={mode.id}
            onClick={() => onSelect(mode.id)}
            style={{
              background: 'rgba(6,13,18,0.8)',
              border: `1px solid ${mode.color}30`,
              borderLeft: `3px solid ${mode.color}`,
              borderRadius: '0 8px 8px 0',
              padding: '16px 18px',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s',
              fontFamily: "'Courier New', monospace",
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = `${mode.color}08`; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(6,13,18,0.8)'; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 16, color: mode.color }}>{mode.icon}</span>
              <div>
                <div style={{ fontSize: 12, color: '#d4c5a9', fontWeight: 500 }}>{mode.label}</div>
                <div style={{ fontFamily: "'Amiri', serif", fontSize: 11, color: `${mode.color}80` }}>{mode.arabicLabel}</div>
              </div>
              <div style={{ marginLeft: 'auto', fontSize: 9, color: mode.color, background: `${mode.color}10`, border: `1px solid ${mode.color}25`, borderRadius: 3, padding: '2px 6px' }}>
                {mode.layers.length} layer{mode.layers.length !== 1 ? 's' : ''}
              </div>
            </div>
            <p style={{ fontSize: 11, color: '#7a6f5d', lineHeight: 1.6, margin: 0 }}>{mode.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function IRLObservationSubmission({ open, onClose, cityId, variant, prefilledTile }: ObsSubmissionProps) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showCosmologyNote, setShowCosmologyNote] = useState(false);
  const [newStep, setNewStep] = useState<string>('');
  const [observationMode, setObservationMode] = useState<ObservationMode | null>(null);
  const [form, setForm] = useState<ObsFormData>({
    title: prefilledTile?.title || '',
    city: cityId,
    encounter: prefilledTile?.encounter || '',
    q_is: '',
    q_what: '',
    q_when: '',
    selectedNames: prefilledTile?.names || [],
    nameRoles: {},
    processName: '',
    processSteps: [],
    zahir: '',
    batin: '',
    haqq: '',
    amanah: '',
    mrgBridges: [],
  });

  useEffect(() => {
    if (open) {
      setStep(0);
      setSubmitted(false);
      setObservationMode(null);
      setForm(prev => ({
        ...prev,
        city: cityId,
        title: prefilledTile?.title || prev.title,
        encounter: prefilledTile?.encounter || prev.encounter,
        selectedNames: prefilledTile?.names || prev.selectedNames,
      }));
    }
  }, [open, cityId, prefilledTile]);

  function updateField<K extends keyof ObsFormData>(key: K, value: ObsFormData[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function toggleName(id: string) {
    setForm(prev => ({
      ...prev,
      selectedNames: prev.selectedNames.includes(id)
        ? prev.selectedNames.filter(n => n !== id)
        : [...prev.selectedNames, id],
    }));
  }

  function toggleMRG(cat: string) {
    setForm(prev => ({
      ...prev,
      mrgBridges: prev.mrgBridges.includes(cat)
        ? prev.mrgBridges.filter(c => c !== cat)
        : [...prev.mrgBridges, cat],
    }));
  }

  function addStep() {
    if (!newStep.trim()) return;
    setForm(prev => ({ ...prev, processSteps: [...prev.processSteps, newStep.trim()] }));
    setNewStep('');
  }

  function removeStep(i: number) {
    setForm(prev => ({ ...prev, processSteps: prev.processSteps.filter((_, idx) => idx !== i) }));
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const payload = {
        title: form.title || 'Untitled Observation',
        city: form.city,
        encounter_observations: [form.encounter],
        q_is: form.q_is,
        q_what: form.q_what,
        q_when: form.q_when,
        selected_names: form.selectedNames,
        name_roles: form.nameRoles,
        process_name: form.processName,
        process_steps: form.processSteps,
        zahir: form.zahir,
        batin: form.batin,
        haqq: form.haqq,
        amanah: form.amanah,
        mrg_bridges: form.mrgBridges,
        variant,
        status: 'submitted',
      };
      const { error } = await supabase.from('irl_observations').insert([payload]);
      if (error) throw error;
      setSubmitted(true);
      setShowCosmologyNote(true);
    } catch {
      // Offline fallback
      const queue = JSON.parse(localStorage.getItem('haal_obs_queue') || '[]');
      queue.push({ ...form, timestamp: Date.now() });
      localStorage.setItem('haal_obs_queue', JSON.stringify(queue));
      setSubmitted(true);
      setShowCosmologyNote(true);
    }
    setSubmitting(false);
  }

  const canAdvance = () => {
    if (step === 0) return form.title.trim().length > 0 && form.encounter.trim().length > 0;
    if (step === 1) return form.q_is.trim().length > 0;
    if (step === 2) return form.selectedNames.length > 0;
    return true;
  };

  if (!open) return null;

  // Determine active layers based on mode
  const activeMode = observationMode ? OBSERVATION_MODES.find(m => m.id === observationMode) : null;
  const activeLayers = activeMode ? LAYER_STEPS.filter(ls => activeMode.layers.includes(ls.id)) : LAYER_STEPS;
  const currentLayer = activeLayers[step] || LAYER_STEPS[step];
  const progress = ((step) / activeLayers.length) * 100;

  return (
    <div
      style={{
        position: 'absolute', inset: 0, background: 'rgba(6,13,18,0.97)',
        zIndex: 50, display: 'flex', flexDirection: 'column',
        fontFamily: "'Courier New', monospace",
      }}
    >
      {/* Mode selector — shown before form if no mode selected */}
      {!observationMode && (
        <ObservationModeSelector onSelect={(mode) => { setObservationMode(mode); setStep(0); }} />
      )}

      {/* Header */}
      <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(61,54,39,0.4)', display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(6,13,18,0.98)' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, color: '#c9a84c', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 2 }}>
            ✦ CATALOG THIS AYAH
            {activeMode && <span style={{ color: `${activeMode.color}`, marginLeft: 6 }}>— {activeMode.label}</span>}
          </div>
          <div style={{ fontSize: 8, color: '#7a6f5d', letterSpacing: '.08em' }}>
            {form.city} · {activeLayers.length} layers
          </div>
        </div>
        {observationMode && (
          <button
            onClick={() => { setObservationMode(null); setStep(0); }}
            style={{ padding: '3px 8px', background: 'transparent', border: '1px solid rgba(61,54,39,0.3)', color: '#7a6f5d', fontSize: 8, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '.06em', marginRight: 4 }}
          >
            ← Mode
          </button>
        )}
        <button
          onClick={onClose}
          style={{ padding: '3px 10px', background: 'transparent', border: '1px solid rgba(61,54,39,0.4)', color: '#7a6f5d', fontSize: 9, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '.06em' }}
        >
          ✕ Close
        </button>
      </div>

      {/* Progress bar */}
      <div style={{ height: 2, background: 'rgba(61,54,39,0.3)' }}>
        <div style={{ height: '100%', background: 'linear-gradient(90deg, #2d7a47, #c9a84c)', width: `${progress}%`, transition: 'width 0.4s ease' }} />
      </div>

      {/* Layer step indicators */}
      <div style={{ padding: '6px 16px', borderBottom: '1px solid rgba(61,54,39,0.2)', display: 'flex', gap: 2, overflowX: 'auto' }}>
        {activeLayers.map((ls, i) => (
          <button
            key={ls.id}
            onClick={() => i <= step || submitted ? setStep(i) : undefined}
            style={{
              padding: '3px 8px', background: 'transparent',
              border: `1px solid ${i === step ? 'rgba(201,168,76,0.5)' : i < step ? 'rgba(45,122,71,0.3)' : 'rgba(61,54,39,0.2)'}`,
              color: i === step ? '#c9a84c' : i < step ? '#2d7a47' : '#7a6f5d',
              fontSize: 8, cursor: i <= step ? 'pointer' : 'default',
              fontFamily: 'inherit', letterSpacing: '.06em', whiteSpace: 'nowrap',
              borderRadius: 2, transition: 'all 0.2s',
            }}
          >
            {i < step ? '✓' : ls.num} {ls.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
        {submitted ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 32, color: '#c9a84c', marginBottom: 12 }}>الْحَمْدُ لِلَّه</div>
            <div style={{ fontSize: 11, color: '#d4c5a9', marginBottom: 6 }}>Observation submitted.</div>
            <div style={{ fontSize: 10, color: '#7a6f5d', lineHeight: 1.6, maxWidth: 300, margin: '0 auto 20px' }}>
              Your recognition has been cataloged. The arc is complete — every recognition returns to gratitude.
            </div>
            {/* Go See For Yourself — terminal instruction */}
            <div style={{ maxWidth: 400, margin: '0 auto 20px', textAlign: 'left' }}>
              <GoSeeForYourselfPrompt context="Your observation is complete. The platform has provided Ilm and Ayn Yaqeen. Now go outside — to the waterfront, to the ward, to the forest. Stand before what you've documented. Haqq Yaqeen is yours to receive." />
            </div>
            <button
              onClick={onClose}
              style={{ padding: '8px 20px', background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', fontSize: 10, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '.1em', textTransform: 'uppercase' }}
            >
              Return to Lens
            </button>
          </div>
        ) : (
          <>
            {/* Layer header */}
            <div style={{ marginBottom: 16, padding: '10px 12px', background: 'rgba(10,25,41,0.3)', border: '1px solid rgba(61,54,39,0.2)', borderRadius: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#c9a84c', flexShrink: 0 }}>
                  {currentLayer.num}
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#c9a84c', letterSpacing: '.1em', textTransform: 'uppercase' }}>{currentLayer.label}</div>
                  <div style={{ fontSize: 12, color: 'rgba(201,168,76,0.4)', fontFamily: 'serif' }}>{currentLayer.ar}</div>
                </div>
              </div>
              <div style={{ fontSize: 10, color: '#7a6f5d', lineHeight: 1.5 }}>{currentLayer.desc}</div>
            </div>

            {/* Step 0: Encounter */}
            {step === 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 8, color: '#7a6f5d', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Title / Name of Encounter</label>
                  <input
                    className="irl-u-inp"
                    value={form.title}
                    onChange={e => updateField('title', e.target.value)}
                    placeholder="e.g. Fruit Tree on Conant Street, Brightmoor Prairie..."
                  />
                </div>
                <div>
                  <label style={{ fontSize: 8, color: '#7a6f5d', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>City</label>
                  <input
                    className="irl-u-inp"
                    value={form.city}
                    onChange={e => updateField('city', e.target.value)}
                    placeholder="City..."
                  />
                </div>
                <div>
                  <label style={{ fontSize: 8, color: '#7a6f5d', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Sensory Encounter — What do you perceive?</label>
                  <textarea
                    className="irl-u-ta"
                    value={form.encounter}
                    onChange={e => updateField('encounter', e.target.value)}
                    placeholder="Describe what you see, hear, smell, feel. The Zahir of the encounter..."
                    rows={4}
                  />
                </div>
              </div>
            )}

            {/* Step 1: Three Questions */}
            {step === 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ padding: '8px 10px', background: 'rgba(10,25,41,0.3)', border: '1px solid rgba(61,54,39,0.2)', borderRadius: 3, marginBottom: 4 }}>
                  <div style={{ fontSize: 9, color: '#d4c5a9', fontStyle: 'italic' }}>&ldquo;{form.encounter || 'Your encounter...'}&rdquo;</div>
                </div>
                <div>
                  <label style={{ fontSize: 8, color: '#2d7a47', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>حُدُوث ذَاتِي — IS IT? (Wujud / Existence)</label>
                  <textarea className="irl-u-ta" value={form.q_is} onChange={e => updateField('q_is', e.target.value)} placeholder="Does this exist? What is its state of being real? If you can reference it, it most certainly is..." rows={2} />
                </div>
                <div>
                  <label style={{ fontSize: 8, color: '#2d8a87', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>حُدُوث صِفَاتِي — WHAT IS IT? (Attributes)</label>
                  <textarea className="irl-u-ta" value={form.q_what} onChange={e => updateField('q_what', e.target.value)} placeholder="What attributes compose it? These are taqdir (measurements) given by Allah — not inherent to the thing..." rows={2} />
                </div>
                <div>
                  <label style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>حُدُوث زَمَانِي — WHEN IS/WAS IT? (Temporal)</label>
                  <textarea className="irl-u-ta" value={form.q_when} onChange={e => updateField('q_when', e.target.value)} placeholder="Place it on the temporal slider. When did it begin? What stages has it passed through? What is it becoming?" rows={2} />
                </div>
              </div>
            )}

            {/* Step 2: Names Fission — add Root Analysis Expander after selection */}
            {step === 2 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {QUICK_NAMES.map(n => (
                    <button
                      key={n.id}
                      onClick={() => toggleName(n.id)}
                      style={{
                        padding: '6px 10px', background: 'transparent',
                        border: `1px solid ${form.selectedNames.includes(n.id) ? 'rgba(201,168,76,0.5)' : 'rgba(61,54,39,0.3)'}`,
                        color: form.selectedNames.includes(n.id) ? '#c9a84c' : '#7a6f5d',
                        fontSize: 9, cursor: 'pointer', fontFamily: 'inherit', borderRadius: 3, transition: 'all 0.15s',
                      }}
                    >
                      <span style={{ fontFamily: "'Amiri', serif", fontSize: 11, marginRight: 4 }}>{n.ar}</span>
                      {n.en}
                    </button>
                  ))}
                </div>

                {/* Root Analysis Expander — shown when Al-Qadir is selected */}
                {form.selectedNames.includes('qadir') && (
                  <RootAnalysisExpander
                    nameId="qadir"
                    nameAr="الْقَادِر"
                    nameEn="Al-Qadir"
                    rootLetters="ق - د - ر"
                    encounterContext={form.title || form.encounter.slice(0, 40)}
                  />
                )}

                {/* Generic root analysis note for any selected name */}
                {form.selectedNames.length > 0 && !form.selectedNames.includes('qadir') && (
                  <div style={{
                    padding: '8px 10px',
                    background: 'rgba(201,168,76,0.04)',
                    border: '1px solid rgba(201,168,76,0.1)',
                    borderRadius: 3,
                    fontSize: 9,
                    color: '#7a6f5d',
                    lineHeight: 1.6,
                  }}>
                    <span style={{ color: '#c9a84c' }}>Root Fission available for Al-Qadir:</span> Select Al-Qadir to access the full 12-dimension root analysis — identifying which meaning dimensions of ق-د-ر are active in this specific encounter.
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Sunnat Allah */}
            {step === 3 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 8, color: '#7a6f5d', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Name this Sunnat Process</label>
                  <input className="irl-u-inp" value={form.processName} onChange={e => updateField('processName', e.target.value)} placeholder="e.g. Mycorrhizal Establishment, Photosynthesis, Metamorphosis..." />
                </div>
                <div>
                  <label style={{ fontSize: 8, color: '#7a6f5d', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Process Steps (ordered)</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 6 }}>
                    {form.processSteps.map((s, i) => (
                      <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: '#c9a84c', flexShrink: 0 }}>{i + 1}</div>
                        <div style={{ flex: 1, fontSize: 10, color: '#d4c5a9' }}>{s}</div>
                        <button onClick={() => removeStep(i)} style={{ background: 'none', border: 'none', color: '#7a6f5d', cursor: 'pointer', fontSize: 10, padding: '0 4px' }}>✕</button>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input className="irl-u-inp" value={newStep} onChange={e => setNewStep(e.target.value)} onKeyDown={e => e.key === 'Enter' && addStep()} placeholder="Add a step... (press Enter)" style={{ flex: 1 }} />
                    <button onClick={addStep} style={{ padding: '4px 10px', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)', color: '#c9a84c', fontSize: 9, cursor: 'pointer', fontFamily: 'inherit' }}>+ Add</button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Zahir / Batin */}
            {step === 4 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 8, color: '#5a5a8a', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>الظَّاهِر — ZAHIR (The Apparent)</label>
                  <textarea className="irl-u-ta" value={form.zahir} onChange={e => updateField('zahir', e.target.value)} placeholder="What is visible, measurable, observable? The surface reality..." rows={3} />
                </div>
                <div style={{ textAlign: 'center', fontSize: 14, color: 'rgba(90,90,138,0.4)' }}>⇄</div>
                <div>
                  <label style={{ fontSize: 8, color: '#5a5a8a', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>الْبَاطِن — BATIN (The Hidden)</label>
                  <textarea className="irl-u-ta" value={form.batin} onChange={e => updateField('batin', e.target.value)} placeholder="What is hidden beneath the surface? The unseen forces, divine decree, Names operating beneath..." rows={3} />
                </div>
              </div>
            )}

            {/* Step 5: Haqq */}
            {step === 5 && (
              <div>
                <div style={{ padding: '8px 10px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 3, marginBottom: 10, fontSize: 9, color: '#7a6f5d', lineHeight: 1.5 }}>
                  Haqq is the truth of what this thing actually is — not what we named it, but what it declares. Complete this sentence: &ldquo;This [thing] is an Ayah declaring that...&rdquo;
                </div>
                <label style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>الْحَقّ — HAQQ Declaration</label>
                <textarea className="irl-u-ta" value={form.haqq} onChange={e => updateField('haqq', e.target.value)} placeholder="This [encounter] is an Ayah declaring that..." rows={4} />
              </div>
            )}

            {/* Step 6: Amanah */}
            {step === 6 && (
              <div>
                <div style={{ padding: '8px 10px', background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.1)', borderRadius: 3, marginBottom: 10, fontSize: 9, color: '#7a6f5d', lineHeight: 1.5 }}>
                  The Amanah is the trust inherent in recognition. You have seen — what does this demand? How does this bridge to community action?
                </div>
                <label style={{ fontSize: 8, color: '#2d7a47', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>الْأَمَانَة — AMANAH</label>
                <textarea className="irl-u-ta" value={form.amanah} onChange={e => updateField('amanah', e.target.value)} placeholder="What trust does this recognition carry? What action does it call for?" rows={4} />
              </div>
            )}

            {/* Step 7: Bridge Links */}
            {step === 7 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 8, color: '#7a6f5d', letterSpacing: '.1em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>MRG Bridge Categories</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {MRG_CATEGORIES.map(cat => (
                      <button
                        key={cat}
                        onClick={() => toggleMRG(cat)}
                        style={{
                          padding: '4px 8px', background: 'transparent',
                          border: `1px solid ${form.mrgBridges.includes(cat) ? 'rgba(45,122,71,0.5)' : 'rgba(61,54,39,0.3)'}`,
                          color: form.mrgBridges.includes(cat) ? '#2d7a47' : '#7a6f5d',
                          fontSize: 9, cursor: 'pointer', fontFamily: 'inherit', borderRadius: 2, transition: 'all 0.15s',
                        }}
                      >
                        {form.mrgBridges.includes(cat) ? '✓ ' : ''}{cat}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div style={{ padding: '10px 12px', background: 'rgba(10,25,41,0.3)', border: '1px solid rgba(61,54,39,0.2)', borderRadius: 4 }}>
                  <div style={{ fontSize: 8, color: '#c9a84c', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>Observation Summary</div>
                  {[
                    { label: 'Title', value: form.title },
                    { label: 'City', value: form.city },
                    { label: 'Names', value: form.selectedNames.map(id => QUICK_NAMES.find(n => n.id === id)?.en || id).join(', ') || 'None selected' },
                    { label: 'Process', value: form.processName || 'Not named' },
                    { label: 'MRG Bridges', value: form.mrgBridges.join(', ') || 'None selected' },
                  ].map(item => (
                    <div key={item.label} style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: 9 }}>
                      <span style={{ color: '#7a6f5d', width: 80, flexShrink: 0 }}>{item.label}:</span>
                      <span style={{ color: '#d4c5a9', flex: 1 }}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Navigation */}
      {!submitted && (
        <div style={{ padding: '10px 16px', borderTop: '1px solid rgba(61,54,39,0.3)', display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(6,13,18,0.98)' }}>
          <button
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            className="irl-u-nav-btn"
          >
            ← Back
          </button>
          <div style={{ flex: 1, textAlign: 'center', fontSize: 9, color: '#7a6f5d' }}>
            Layer {step + 1} of {activeLayers.length}
          </div>
          {step < activeLayers.length - 1 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canAdvance()}
              className="irl-u-nav-btn"
              style={{ borderColor: canAdvance() ? 'rgba(201,168,76,0.3)' : undefined, color: canAdvance() ? '#c9a84c' : undefined }}
            >
              Next →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="irl-u-submit-btn"
              style={{ width: 'auto', padding: '6px 20px' }}
            >
              {submitting ? 'Submitting...' : '✦ Catalog This Ayah'}
            </button>
          )}
        </div>
      )}

      {/* Submitted state */}
      {submitted && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 28, color: '#c9a84c', marginBottom: 12 }}>✦</div>
            <div style={{ fontSize: 12, color: '#d4c5a9', fontFamily: "'Georgia', serif", fontStyle: 'italic', marginBottom: 6 }}>
              "You recognized what the Pen wrote."
            </div>
            <div style={{ fontSize: 9, color: '#7a6f5d', letterSpacing: '.1em', textTransform: 'uppercase' }}>
              Observation cataloged — {form.title || 'Untitled'}
            </div>
          </div>

          {/* Cosmology Note */}
          <ObservationCompleteCosmologyNote
            visible={showCosmologyNote}
            onDismiss={() => setShowCosmologyNote(false)}
          />

          <GoSeeForYourselfPrompt />

          <button
            onClick={onClose}
            style={{
              width: '100%', padding: '10px', background: 'rgba(201,168,76,0.08)',
              border: '1px solid rgba(201,168,76,0.25)', color: '#c9a84c',
              fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
              cursor: 'pointer', fontFamily: 'inherit', borderRadius: 4,
            }}
          >
            Return to Garden
          </button>
        </div>
      )}
    </div>
  );
}
